import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Review } from '../../model/professional/reviews-model';
import { Star, MessageSquare, Check, X } from 'lucide-react';
import reviewsViewModel from '../../view-model/professional/reviews-view-model';
import { useTranslation } from 'react-i18next';

interface ReviewItemProps {
  review: Review;
}

const ReviewItem: React.FC<ReviewItemProps> = observer(({ review }) => {
  const { t } = useTranslation();
  const [isResponding, setIsResponding] = useState(false);
  const [response, setResponse] = useState(review.response || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fonction pour générer les étoiles
  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            className={`${
              star <= rating
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-gray-300 dark:text-gray-600'
            }`}
          />
        ))}
      </div>
    );
  };

  const handleSubmitResponse = async () => {
    if (!response.trim()) return;
    
    setIsSubmitting(true);
    try {
      const success = await reviewsViewModel.respondToReview(review.id, response);
      if (success) {
        setIsResponding(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelResponse = () => {
    setIsResponding(false);
    setResponse(review.response || '');
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
      <div className="flex justify-between items-start mb-4">
        <div>
          <div className="flex items-center mb-1">
            {renderStars(review.rating)}
            <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              {review.rating}/5
            </span>
          </div>
          <h4 className="font-medium text-gray-900 dark:text-white">
            {review.client?.firstName} {review.client?.lastName ? review.client.lastName.charAt(0) + '.' : ''}
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {reviewsViewModel.formatDate(review.createdAt)}
            {review.booking?.date && (
              <span className="ml-2">
                • {t('reviews.bookingDate')} {reviewsViewModel.formatDate(review.booking.date)}
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="mb-4">
        <p className="text-gray-700 dark:text-gray-300">{review.comment}</p>
      </div>

      {/* Réponse existante */}
      {review.response && !isResponding && (
        <div className="mt-4 pl-4 border-l-2 border-primary">
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">{t('reviews.yourReply')} : </p>
          <p className="text-sm text-gray-700 dark:text-gray-300">{review.response}</p>
          <button
            onClick={() => setIsResponding(true)}
            className="mt-2 text-xs text-primary hover:text-primary-light dark:text-primary-dark dark:hover:text-primary-light flex items-center"
          >
            <MessageSquare size={12} className="mr-1" /> {t('reviews.modifyReply')}
          </button>
        </div>
      )}

      {/* Bouton pour répondre */}
      {!review.response && !isResponding && (
        <button
          onClick={() => setIsResponding(true)}
          className="mt-2 text-sm text-primary hover:text-primary-light dark:text-primary-dark dark:hover:text-primary-light flex items-center"
        >
          <MessageSquare size={14} className="mr-1" /> {t('reviews.replyReview')}
        </button>
      )}

      {/* Formulaire de réponse */}
      {isResponding && (
        <div className="mt-4">
          <label htmlFor={`response-${review.id}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('reviews.yourReply')}
          </label>
          <textarea
            id={`response-${review.id}`}
            value={response}
            onChange={(e) => setResponse(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
            rows={3}
            placeholder={t('reviews.writeYourReply') || ''}
            disabled={isSubmitting}
          ></textarea>
          <div className="flex justify-end mt-2 space-x-2">
            <button
              onClick={handleCancelResponse}
              className="px-3 py-1 text-xs font-medium text-gray-700 bg-primary/10 rounded-md hover:bg-primary/20 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 flex items-center"
              disabled={isSubmitting}
            >
              <X size={12} className="mr-1" /> {t('reviews.cancel')}
            </button>
            <button
              onClick={handleSubmitResponse}
              className="px-3 py-1 text-xs font-medium text-white bg-primary rounded-md hover:bg-primary/80 dark:bg-primary-dark/80 dark:hover:bg-primary-dark/60 flex items-center disabled:opacity-50"
              disabled={isSubmitting || !response.trim()}
            >
              <Check size={12} className="mr-1" /> {isSubmitting ? t('reviews.sending') : t('reviews.send')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

export default ReviewItem;
